import { useMemo } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';

import { ROLE_NAME_MAX_LENGTH, validateRoleName } from '@personnel/domain/value-objects/RoleName';
import { MIN_PERMISSIONS_REQUIRED } from '@personnel/domain/value-objects/RolePermissions';

/**
 * Hook that provides Yup validation schema for role forms
 * Reuses domain validation rules to ensure consistency
 *
 * @returns {Object} Resolver and schema for react-hook-form
 *
 * @example
 * const { resolver } = useRoleSchema();
 * const methods = useForm({ resolver });
 */
export const useRoleSchema = () => {
  const { t } = useTranslation('roles');
  const { t: tCommon } = useTranslation('common');

  const schema = useMemo(
    () =>
      yup.object({
        // Role Name - Uses domain validation
        name: yup
          .string()
          .required(tCommon('messages.requireField'))
          .max(ROLE_NAME_MAX_LENGTH, t('form.nameMaxLength'))
          .test('domain-validation', '', (value) => {
            const result = validateRoleName(value || '');
            if (!result.valid) {
              return new yup.ValidationError(result.error || 'Invalid role name', value, 'name');
            }
            return true;
          })
          .trim(),

        // Permissions - Uses domain business rule
        permissions: yup
          .array()
          .of(yup.string())
          .min(MIN_PERMISSIONS_REQUIRED, t('form.permissionsAtLeastOne'))
          .required(tCommon('messages.requireField')),
      }),
    [t, tCommon],
  );

  return {
    resolver: yupResolver(schema),
    schema,
  };
};
